import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { Tag, ArrowRight, CheckCircle2, Phone, Car, Plug, AlertTriangle, BadgePercent } from "lucide-react";
import { SITE } from "../../site.config";
import { PRICING } from "../../data/pricing";

export default function PriceList() {
  // Inject OfferCatalog JSON-LD for rich-result eligibility
  useEffect(() => {
    const data = {
      "@context": "https://schema.org",
      "@type": "OfferCatalog",
      name: "Powerz Auto Electrical — Towing Electrics Pricing",
      itemListElement: PRICING.jobs.map((j) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: `${j.name}${j.variant ? " — " + j.variant : ""}`,
          description: j.desc,
        },
        price: j.price.replace(/[^0-9.]/g, ""),
        priceCurrency: "AUD",
        priceSpecification: {
          "@type": "PriceSpecification",
          price: j.price.replace(/[^0-9.]/g, ""),
          priceCurrency: "AUD",
          valueAddedTaxIncluded: true,
        },
        availability: "https://schema.org/InStock",
        url: "https://powerzautoelectrical.com.au/#pricing",
      })),
    };
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.dataset.routeJsonld = "1";
    s.textContent = JSON.stringify(data);
    document.head.appendChild(s);
    return () => {
      s.parentNode && s.parentNode.removeChild(s);
    };
  }, []);

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative bg-black py-24 md:py-32 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-cyan-pz" />
              <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
                JOBS & PRICING
              </span>
            </div>
            <h2 className="bebas text-white text-5xl md:text-7xl leading-[0.9] max-w-4xl">
              UPFRONT PRICES, <br />
              <span className="text-cyan-pz">NO SURPRISES.</span>
            </h2>
            <p
              data-testid="pricing-tagline"
              className="mt-5 inline-flex items-center gap-2 bebas text-cyan-pz tracking-widest text-sm md:text-base border border-cyan-pz/40 bg-cyan-pz/5 px-4 py-2 clip-tag"
            >
              ★ {PRICING.tagline}
            </p>
          </div>
          <p className="text-white/60 max-w-md barlow text-base md:text-lg">
            {PRICING.intro}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {PRICING.jobs.map((job, i) => (
            <article
              key={job.slug}
              data-testid={`pricing-${job.slug}`}
              className="relative bg-[#0A0A0A] border border-white/10 hover:border-cyan-pz transition-colors clip-perf flex flex-col overflow-hidden"
            >
              {/* Product image */}
              <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-[#0F0F0F] to-black border-b border-white/10">
                {job.image ? (
                  <img
                    src={job.image}
                    alt={`${job.name}${job.variant ? " " + job.variant : ""} — supply & install Sunshine Coast`}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: job.imagePosition || "center" }}
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2 relative">
                    <div className="absolute inset-0 grain opacity-50" aria-hidden="true" />
                    <div className="absolute inset-0 diag-line opacity-20" aria-hidden="true" />
                    <Plug className="w-14 h-14 text-cyan-pz/40 relative z-10" />
                    <p className="bebas text-white/40 tracking-widest text-xs relative z-10">
                      PHOTO COMING SOON
                    </p>
                  </div>
                )}
                <div className="absolute top-3 right-3 bg-black/90 border-2 border-cyan-pz text-cyan-pz px-4 py-2 clip-tag shadow-[0_0_20px_rgba(94,230,230,0.4)]">
                  <p className="bebas tracking-wide text-3xl md:text-4xl leading-none">
                    {job.priceTiers ? `FROM ${job.priceTiers[0].price}` : job.price}
                  </p>
                  <p className="text-[10px] text-white/70 tracking-widest text-right leading-none mt-1">
                    GST INC
                  </p>
                </div>
              </div>

              <div className="p-6 md:p-8 flex-1 flex flex-col">
                <div className="mb-4">
                  <div className="inline-flex items-center gap-2 mb-3 bg-cyan-pz/10 border border-cyan-pz/30 px-3 py-1 clip-tag">
                    <Tag className="w-3.5 h-3.5 text-cyan-pz" />
                    <span className="bebas text-cyan-pz tracking-widest text-[10px]">
                      {job.tag || "SUPPLY + INSTALL"}
                    </span>
                  </div>
                  <h3 className="bebas text-white text-2xl md:text-3xl tracking-wide leading-tight">
                    {job.name}
                  </h3>
                  {job.variant && (
                    <p className="bebas text-cyan-pz/80 tracking-widest text-xs mt-1">
                      {job.variant.toUpperCase()}
                    </p>
                  )}
                  {job.priceTiers ? (
                    <div className="mt-4 space-y-2" data-testid={`pricing-tiers-${job.slug}`}>
                      {job.priceTiers.map((tier) => (
                        <div
                          key={tier.label}
                          className="flex items-baseline justify-between gap-4 border-b border-white/10 pb-2 last:border-b-0"
                        >
                          <span className="bebas text-white/80 tracking-widest text-sm md:text-base">
                            {tier.label.toUpperCase()}
                          </span>
                          <span className="bebas text-cyan-pz text-3xl md:text-4xl leading-none glow-cyan">
                            {tier.price}
                          </span>
                        </div>
                      ))}
                      <p className="bebas text-white/50 tracking-widest text-xs pt-1">
                        ALL PRICES GST INCLUDED
                      </p>
                    </div>
                  ) : (
                    <div className="mt-4 flex items-baseline gap-3">
                      <span
                        data-testid={`pricing-price-${job.slug}`}
                        className="bebas text-cyan-pz text-5xl md:text-6xl leading-none glow-cyan"
                      >
                        {job.price}
                      </span>
                      <span className="bebas text-white/50 tracking-widest text-xs">
                        GST INCLUDED
                      </span>
                    </div>
                  )}
                </div>

                <p className="text-white/65 barlow text-sm leading-relaxed mb-5">
                  {job.desc}
                </p>

                {job.shopCategory && (
                  <Link
                    to={`/shop?category=${job.shopCategory}`}
                    data-testid={`pricing-shop-link-${job.slug}`}
                    className="inline-flex items-center gap-1.5 bebas tracking-widest text-xs text-cyan-pz/80 hover:text-cyan-pz transition-colors mb-5"
                  >
                    BROWSE RELATED PARTS
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                )}

                {job.notes && job.notes.length > 0 && (
                  <div
                    data-testid={`pricing-notes-${job.slug}`}
                    className="space-y-3 mb-5"
                  >
                    {job.notes.map((n, idx) => {
                      const isLegal = n.type === "legal";
                      const Icon = isLegal ? AlertTriangle : BadgePercent;
                      return (
                        <div
                          key={idx}
                          className={`p-4 border ${
                            isLegal
                              ? "border-amber-400/40 bg-amber-400/5"
                              : "border-cyan-pz/40 bg-cyan-pz/5"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <Icon
                              className={`w-5 h-5 shrink-0 mt-0.5 ${
                                isLegal ? "text-amber-400" : "text-cyan-pz"
                              }`}
                            />
                            <div>
                              <p
                                className={`bebas tracking-widest text-xs mb-1 ${
                                  isLegal ? "text-amber-400" : "text-cyan-pz"
                                }`}
                              >
                                {n.title}
                              </p>
                              <p className="text-white/75 barlow text-xs leading-relaxed">
                                {n.body}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {job.vehicles && (
                  <Accordion type="single" collapsible className="mt-auto">
                    <AccordionItem
                      value={`vehicles-${i}`}
                      className="border-t border-white/10"
                    >
                      <AccordionTrigger
                        data-testid={`pricing-vehicles-${job.slug}`}
                        className="bebas tracking-widest text-sm text-cyan-pz hover:text-white hover:no-underline py-4"
                      >
                        <span className="flex items-center gap-2">
                          <Car className="w-4 h-4" />
                          Compatible Vehicles
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="pb-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {job.vehicles.map((g) => (
                            <div key={g.make}>
                              <p className="bebas text-white tracking-widest text-sm mb-2 border-b border-cyan-pz/20 pb-1">
                                {g.make}
                              </p>
                              <ul className="space-y-1.5">
                                {g.models.map((m) => (
                                  <li
                                    key={m}
                                    className="text-white/65 barlow text-xs leading-snug flex gap-2 items-start"
                                  >
                                    <CheckCircle2 className="w-3 h-3 text-cyan-pz mt-1 shrink-0" />
                                    <span>{m}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                        <p className="text-white/40 barlow text-[11px] mt-4 italic">
                          Vehicle not on the list? Give us a call —
                          we'll let you know if your model fits the basic or
                          smart install spec.
                        </p>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                )}
              </div>
            </article>
          ))}
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 bg-[#0A0A0A] border border-white/10 p-6 clip-perf">
            <p className="bebas text-cyan-pz tracking-widest text-xs mb-2">
              ANYTHING ELSE
            </p>
            <p className="text-white/80 barlow">
              Custom 4WD fitouts, dual battery, lithium upgrades, A/C
              diagnostics and any work outside the table above are quoted on
              the day. Workshop rate{" "}
              <span className="text-cyan-pz bebas tracking-wider">
                {SITE.business.hourlyRate} ({SITE.business.hourlyRateNote})
              </span>
              .
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <a
              href={`tel:${SITE.business.phoneTel}`}
              data-testid="pricing-call-btn"
              className="inline-flex items-center justify-center gap-2 bg-cyan-pz text-black px-6 py-4 bebas text-base tracking-widest hover:bg-white transition-colors clip-perf"
            >
              <Phone className="w-4 h-4" />
              CALL FOR A QUOTE
            </a>
            <a
              href={`mailto:${SITE.business.email}?subject=Powerz%20Auto%20Electrical%20Pricing%20Enquiry`}
              data-testid="pricing-quote-btn"
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-6 py-4 bebas text-base tracking-widest hover:border-cyan-pz hover:text-cyan-pz transition-colors"
            >
              EMAIL US
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
